import React, { useCallback, useEffect, useState } from 'react';
import { TenantList } from '../vendor/prism-components';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Shared/Toast';
import OnPremLicenseForm from '../components/license/OnPremLicenseForm';
import OnPremDeploymentList from '../components/license/OnPremDeploymentList';
import SaasLicensePanel from '../components/license/SaasLicensePanel';
import HeartbeatMonitorPanel from '../components/license/HeartbeatMonitorPanel';
import LicenseJwtModal from '../components/license/LicenseJwtModal';
import ConfirmDialog from '../components/license/ConfirmDialog';
import { PLATFORM_ENVIRONMENTS } from '../constants/environments';

// Umgebungen fuer die OnPrem-Reissue-Maske inkl. "onprem" (On-Prem-Auslieferung),
// konsistent zum Ausstell-Dialog (OnPremLicenseForm).
const ONPREM_ENVIRONMENTS = [...PLATFORM_ENVIRONMENTS, 'onprem'];

/**
 * LicenseManagerPage - vollwertiger Lizenzmanager (PINFRA-2729 / 2784, Epic 2371).
 *
 * Vier Faehigkeiten:
 *   Erstellen   -> ONPREM-Lizenz ausstellen (OnPremLicenseForm + LicenseJwtModal)
 *   Anzeigen    -> ONPREM-Deployments je Mandant + SaaS-Lizenzen (LicenseEditor)
 *   Ueberwachen -> Heartbeat-Alarme + aggregierte Deployments (HeartbeatMonitorPanel)
 *   Erweitern   -> Verlaengern/Folgelizenz/revoke/approve/status/reset-instance
 *
 * Hooks (siehe docs/PLAN-block3-tenant-license-ui.md):
 *   - ONPREM + SaaS-Lizenzen: useApi  -> /api/platform/admin/...  (PlatformTenant-id-Raum)
 *     (gilt plattformweit; auch TenantManagementPage laeuft ueber useApi.)
 *
 * Guard: wie TenantManagementPage unter /admin (isAdmin). ONPREM-Mutationen sind
 * platform_admin-only (Backend 403 fuer org_admin) -> UI deaktiviert sie fuer
 * nicht-platform_admin und zeigt einen Hinweis.
 */

const SUBTABS = ['onprem', 'saas'];
const STATUS_OPTIONS = ['active', 'migrating', 'decommissioned'];

export default function LicenseManagerPage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const { showToast } = useToast();
  const { userRole } = useAuth();
  const canManage = userRole === 'platform_admin';

  const [view, setView] = useState('tenants');   // tenants | monitor
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [subtab, setSubtab] = useState('onprem');

  // ONPREM-State
  const [deployments, setDeployments] = useState([]);
  const [depLoading, setDepLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issueResult, setIssueResult] = useState(null);   // Antwort der Ausstellung -> Modal
  const [action, setAction] = useState(null);             // { type, dep }
  const [actionInput, setActionInput] = useState('');     // Datum / status / license_file
  const [editForm, setEditForm] = useState({});           // Bearbeiten-Dialog (Metadaten + Konditionen)
  const [actionBusy, setActionBusy] = useState(false);

  // ─── Loader ────────────────────────────────────────────────────────────────
  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/admin/tenants');
      setTenants(Array.isArray(data) ? data : (data?.items || []));
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const loadDeployments = useCallback(async (tenantId) => {
    if (!tenantId) return;
    setDepLoading(true);
    try {
      const res = await api(`/admin/tenants/${tenantId}/onprem-licenses`);
      setDeployments(res?.deployments || []);
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
      setDeployments([]);
    } finally {
      setDepLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => {
    if (selected?.id) {
      setSubtab('onprem');
      loadDeployments(selected.id);
    }
  }, [selected?.id, loadDeployments]);

  // ─── Erstellen ───────────────────────────────────────────────────────────────
  const handleIssue = async (body) => {
    if (!selected) return;
    setIssuing(true);
    try {
      const res = await api(`/admin/tenants/${selected.id}/onprem-licenses`, { method: 'POST', body });
      setIssueResult({ ...res, _tool: body.tool_id });
      showToast(
        (res.signed
          ? t('license.issue.ok_signed', 'Lizenz signiert ausgestellt')
          : t('license.issue.ok_unsigned', 'Claims ausgestellt (unsigniert)'))
        + (res.tool_license_autocreated
            ? ' ' + t('license.issue.autocreated', '(Tool-Lizenz automatisch angelegt)')
            : ''),
        'success'
      );
      await loadDeployments(selected.id);
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setIssuing(false);
    }
  };

  // ─── Erweitern / Verwalten ────────────────────────────────────────────────────
  const openAction = (type, dep) => {
    setAction({ type, dep });
    setActionInput(type === 'status' ? (dep.status || 'active') : (type === 'extend' ? (dep.valid_until || '') : ''));
    if (type === 'edit') {
      setEditForm({
        // Metadaten (direkt editierbar, kein Re-Issue)
        status: dep.status || 'active',
        owner_contact: dep.owner_contact || '',
        notes: dep.notes || '',
        // Konditionen (stellen bei Aenderung einen neuen Token aus)
        host: dep.host || '',
        environment: dep.environment || 'prod',
        binding: dep.binding || 'soft',
        valid_until: dep.valid_until || '',
        reissue: false,
      });
    }
  };

  const runAction = async () => {
    if (!action || !selected) return;
    const { type, dep } = action;
    setActionBusy(true);
    try {
      if (type === 'edit') {
        // Metadaten direkt + Konditionen loesen serverseitig einen Re-Issue aus.
        const body = {
          status: editForm.status,
          owner_contact: editForm.owner_contact,
          notes: editForm.notes,
          host: editForm.host,
          environment: editForm.environment,
          binding: editForm.binding,
          valid_until: editForm.valid_until || null,
          reissue: !!editForm.reissue,
        };
        const res = await api(`/admin/onprem-licenses/${dep.deployment_id}`, { method: 'PUT', body });
        if (res?.reissued) {
          // Neuer Token ausgestellt -> wie bei Ausstellung/Verlaengerung anzeigen.
          setIssueResult({ ...res, _tool: dep.tool_id });
          showToast(t('license.onprem.edit.ok_reissued', 'Gespeichert - neue Lizenz ausgestellt'), 'success');
        } else {
          showToast(t('license.onprem.edit.ok', 'Aenderungen gespeichert'), 'success');
        }
      } else if (type === 'extend') {
        // Verlaengern = Neu-Ausstellung mit gleicher instance_id und neuem valid_until
        // (das Issue-Endpoint macht ein Upsert ueber (tenant, tool, instance_id) und re-signiert).
        if (!actionInput) throw new Error(t('license.onprem.extend.date_required', 'Neues Ablaufdatum erforderlich'));
        const body = {
          tool_id: dep.tool_id, host: dep.host, environment: dep.environment,
          binding: dep.binding, instance_id: dep.instance_id, valid_until: actionInput,
        };
        const res = await api(`/admin/tenants/${selected.id}/onprem-licenses`, { method: 'POST', body });
        setIssueResult({ ...res, _tool: dep.tool_id });
        showToast(t('license.onprem.extend.ok', 'Lizenz verlaengert / neu ausgestellt'), 'success');
      } else if (type === 'follow-up') {
        if (!actionInput.trim()) throw new Error(t('license.onprem.followup.required', 'Vorab signierte Lizenz erforderlich'));
        let license_file = actionInput.trim();
        try { license_file = JSON.parse(license_file); } catch { /* als String (JWS compact) deponieren */ }
        await api(`/admin/onprem-licenses/${dep.deployment_id}/follow-up-license`, { method: 'POST', body: { license_file } });
        showToast(t('license.onprem.followup.ok', 'Folgelizenz deponiert'), 'success');
      } else if (type === 'approve') {
        await api(`/admin/onprem-licenses/${dep.deployment_id}/approve`, { method: 'POST' });
        showToast(t('license.onprem.approve.ok', 'Deployment freigegeben'), 'success');
      } else if (type === 'status') {
        await api(`/admin/onprem-licenses/${dep.deployment_id}/status`, { method: 'POST', body: { status: actionInput } });
        showToast(t('license.onprem.status.ok', 'Status gesetzt'), 'success');
      } else if (type === 'reset-instance') {
        await api(`/admin/onprem-licenses/${dep.deployment_id}/reset-instance`, { method: 'POST' });
        showToast(t('license.onprem.reset.ok', 'Instanz-Bindung zurueckgesetzt'), 'success');
      } else if (type === 'revoke') {
        await api(`/admin/onprem-licenses/${dep.deployment_id}/revoke`, { method: 'POST' });
        showToast(t('license.onprem.revoke.ok', 'Lizenz widerrufen'), 'success');
      }
      setAction(null);
      setActionInput('');
      setEditForm({});
      await loadDeployments(selected.id);
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setActionBusy(false);
    }
  };

  // ─── Render: Action-Dialog-Konfiguration ──────────────────────────────────────
  const ACTION_META = {
    'edit':           { title: t('license.onprem.action.edit', 'Bearbeiten'),                 confirm: t('common.save', 'Speichern'),                     danger: false },
    'extend':         { title: t('license.onprem.action.extend', 'Verlaengern'),              confirm: t('license.onprem.action.extend', 'Verlaengern'),  danger: false },
    'follow-up':      { title: t('license.onprem.action.follow_up', 'Folgelizenz deponieren'), confirm: t('common.save', 'Speichern'),                     danger: false },
    'approve':        { title: t('license.onprem.action.approve', 'Freigeben'),               confirm: t('license.onprem.action.approve', 'Freigeben'),   danger: false },
    'status':         { title: t('license.onprem.action.status', 'Status setzen'),            confirm: t('common.save', 'Speichern'),                     danger: false },
    'reset-instance': { title: t('license.onprem.action.reset', 'Instanz ruecksetzen'),       confirm: t('license.onprem.action.reset', 'Instanz ruecksetzen'), danger: true },
    'revoke':         { title: t('license.onprem.action.revoke', 'Widerrufen'),               confirm: t('license.onprem.action.revoke', 'Widerrufen'),   danger: true },
  };

  const meta = action ? ACTION_META[action.type] : null;

  const viewTabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setView(key)}
      style={{
        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        background: view === key ? 'var(--bg-card)' : 'transparent',
        color: view === key ? 'var(--accent-hover)' : 'var(--text-muted)',
        border: '1px solid transparent',
        borderBottom: view === key ? '2px solid var(--accent)' : '2px solid transparent',
        borderRadius: '8px 8px 0 0',
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* View-Umschalter: Mandanten-Verwaltung vs. Ueberwachung */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid var(--border)' }}>
        {viewTabBtn('tenants', t('license.view.tenants', 'Mandanten & Lizenzen'))}
        {viewTabBtn('monitor', t('license.view.monitor', 'Ueberwachung'))}
      </div>

      {!canManage && (
        <div
          style={{
            background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)',
            borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#b45309', marginBottom: 16,
          }}
        >
          {t('license.readonly_hint', 'Nur-Lese-Ansicht - Lizenz-Ausstellung und -Verwaltung erfordern die Rolle platform_admin.')}
        </div>
      )}

      {view === 'monitor' ? (
        <HeartbeatMonitorPanel api={api} showToast={showToast} t={t} />
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Links: Mandantenliste */}
          <div style={{ flex: '0 0 320px' }}>
            <TenantList
              tenants={tenants}
              selected={selected}
              onSelect={setSelected}
              loading={loading}
              t={t}
            />
          </div>

          {/* Rechts: Detail-Panel */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!selected ? (
              <div style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
              }}>
                {t('license.detail_empty', 'Mandant auswaehlen, um Lizenzen zu verwalten.')}
              </div>
            ) : (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{selected.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {selected.slug} - {selected.id}
                  </div>
                </div>

                {/* Sub-Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                  {SUBTABS.map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setSubtab(tab)}
                      style={{
                        padding: '10px 16px', fontSize: 12, fontWeight: 600, background: 'transparent',
                        color: subtab === tab ? 'var(--accent-hover)' : 'var(--text-muted)',
                        border: 'none',
                        borderBottom: subtab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                        cursor: 'pointer',
                      }}
                    >
                      {t(`license.subtab.${tab}`, tab)}
                    </button>
                  ))}
                </div>

                <div style={{ padding: 20 }}>
                  {subtab === 'onprem' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                      <section>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                          {t('license.onprem.create_title', 'ONPREM-Lizenz erstellen')}
                        </div>
                        <OnPremLicenseForm onSubmit={handleIssue} submitting={issuing} disabled={!canManage} t={t} />
                      </section>
                      <section>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                          {t('license.onprem.list_title', 'ONPREM-Deployments')}
                        </div>
                        <OnPremDeploymentList
                          deployments={deployments}
                          loading={depLoading}
                          onAction={openAction}
                          canManage={canManage}
                          t={t}
                        />
                      </section>
                    </div>
                  )}
                  {subtab === 'saas' && (
                    <SaasLicensePanel
                      tenantId={selected.id}
                      api={api}
                      canManage={canManage}
                      showToast={showToast}
                      t={t}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ergebnis der Ausstellung / Verlaengerung */}
      {issueResult && (
        <LicenseJwtModal
          result={issueResult}
          tenantLabel={selected?.slug || selected?.name}
          toolId={issueResult._tool}
          onClose={() => setIssueResult(null)}
          t={t}
        />
      )}

      {/* Aktions-Dialog (Erweitern/Verwalten) */}
      {action && meta && (
        <ConfirmDialog
          title={meta.title}
          message={t(`license.onprem.confirm.${action.type.replace('-', '_')}`, '')}
          confirmLabel={meta.confirm}
          cancelLabel={t('common.cancel', 'Abbrechen')}
          danger={meta.danger}
          busy={actionBusy}
          onConfirm={runAction}
          onCancel={() => { setAction(null); setActionInput(''); setEditForm({}); }}
        >
          {action.type === 'edit' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Metadaten - direkt editierbar, kein neuer Token */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                  {t('license.onprem.edit.meta_title', 'Metadaten')}
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.status.label', 'Status')}</label>
                  <select className="field-input" value={editForm.status || 'active'}
                    onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{t(`license.onprem.status_val.${s}`, s)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.edit.owner', 'Kontakt / Owner')}</label>
                  <input className="field-input" type="text" value={editForm.owner_contact || ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, owner_contact: e.target.value }))}
                    placeholder={t('license.onprem.edit.owner_ph', 'z.B. Vivavis-Auslieferung, Ansprechpartner')} />
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.edit.notes', 'Notizen')}</label>
                  <textarea className="field-input" value={editForm.notes || ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                    style={{ minHeight: 60 }} />
                </div>
              </div>

              {/* Konditionen - Aenderung stellt eine neue signierte Lizenz aus */}
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>
                  {t('license.onprem.edit.cond_title', 'Konditionen aendern (stellt neu aus)')}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 8 }}>
                  {t('license.onprem.edit.cond_hint', 'Aenderungen an Host, Environment, Bindung oder Gueltigkeit stellen automatisch einen neuen signierten Token aus. Seats/Tier/Features stammen aus der SaaS-Toollizenz - ein Neu-Ausstellen zieht die aktuellen Werte.')}
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.edit.host', 'Host')}</label>
                  <input className="field-input" type="text" value={editForm.host || ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, host: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.edit.environment', 'Environment')}</label>
                  <select className="field-input" value={editForm.environment || 'onprem'}
                    onChange={(e) => setEditForm((f) => ({ ...f, environment: e.target.value }))}>
                    {/* bestehenden (ggf. abweichenden) Wert mit anbieten, sonst die Standardliste */}
                    {(ONPREM_ENVIRONMENTS.includes(editForm.environment) || !editForm.environment
                      ? ONPREM_ENVIRONMENTS
                      : [editForm.environment, ...ONPREM_ENVIRONMENTS]
                    ).map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.edit.binding', 'Bindung')}</label>
                  <select className="field-input" value={editForm.binding || 'soft'}
                    onChange={(e) => setEditForm((f) => ({ ...f, binding: e.target.value }))}>
                    <option value="soft">soft</option>
                    <option value="hard">hard</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="field-label">{t('license.onprem.edit.valid_until', 'Gueltig bis')}</label>
                  <input className="field-input" type="date" value={editForm.valid_until || ''}
                    onChange={(e) => setEditForm((f) => ({ ...f, valid_until: e.target.value }))} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!editForm.reissue}
                    onChange={(e) => setEditForm((f) => ({ ...f, reissue: e.target.checked }))} />
                  {t('license.onprem.edit.force_reissue', 'Neu ausstellen erzwingen (aktuelle Toollizenz-Entitlements ziehen)')}
                </label>
              </div>
            </div>
          )}
          {action.type === 'extend' && (
            <div className="form-group">
              <label className="field-label">{t('license.onprem.extend.new_date', 'Neues Ablaufdatum')}</label>
              <input className="field-input" type="date" value={actionInput} onChange={(e) => setActionInput(e.target.value)} />
            </div>
          )}
          {action.type === 'status' && (
            <div className="form-group">
              <label className="field-label">{t('license.onprem.status.label', 'Neuer Status')}</label>
              <select className="field-input" value={actionInput} onChange={(e) => setActionInput(e.target.value)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{t(`license.onprem.status_val.${s}`, s)}</option>
                ))}
              </select>
            </div>
          )}
          {action.type === 'follow-up' && (
            <div className="form-group">
              <label className="field-label">{t('license.onprem.followup.label', 'Vorab signierte Folgelizenz (JWS oder JSON)')}</label>
              <textarea
                className="field-input"
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 11.5 }}
                placeholder="eyJ... (JWS Compact) oder {...}"
              />
            </div>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}
